import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Trash2, RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { projectsApi } from '../api';
import { statusLabel, typeLabel, fmtDate } from '../constants';

export const RecentlyDeletedDialog = ({ open, onOpenChange, onChanged }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    projectsApi.trashList()
      .then((r) => setEntries(r.data))
      .catch(() => toast.error('Could not load Recently Deleted'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const restore = async (e) => {
    if (busy) return;
    setBusy(true);
    try {
      await projectsApi.trashRestore(e.trash_id);
      toast.success(`"${e.name}" restored with all its data`);
      load();
      onChanged?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not restore project');
    } finally { setBusy(false); }
  };

  const confirmPurge = async () => {
    try {
      await projectsApi.trashPurge(purging.trash_id);
      toast.success(`"${purging.name}" permanently deleted`);
      setPurging(null);
      load();
    } catch { toast.error('Could not delete'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111111] border-gray-800 text-white w-[calc(100vw-2rem)] max-w-xl max-h-[90vh] overflow-y-auto" data-testid="recently-deleted-dialog">
        <DialogHeader>
          <DialogTitle>Recently Deleted</DialogTitle>
          <DialogDescription className="text-gray-500">
            Deleted projects are kept here for 30 days with all their data, then removed forever.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-10 text-gray-500"><Loader2 size={20} className="mx-auto animate-spin text-[#D4AF37]" /></div>
        ) : entries.length === 0 ? (
          <div className="text-center py-10" data-testid="trash-empty">
            <Trash2 size={28} className="mx-auto mb-2 text-gray-700" />
            <p className="text-sm text-gray-500">Nothing in Recently Deleted.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="trash-list">
            {entries.map((e) => (
              <div key={e.trash_id} className="flex flex-wrap items-center gap-3 bg-[#161616] border border-gray-800 rounded-md px-4 py-3" data-testid={`trash-row-${e.trash_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white font-medium truncate">{e.name}</div>
                  <div className="text-[11px] text-gray-500">
                    {typeLabel(e.project_type)}{e.customer_name ? ` · ${e.customer_name}` : ''} · was {statusLabel(e.status)} · deleted {fmtDate(e.deleted_at?.slice(0, 10))}
                  </div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border shrink-0 ${e.days_left <= 5 ? 'bg-red-900/30 text-red-400 border-red-800' : 'bg-gray-800 text-gray-400 border-gray-700'}`}>
                  {e.days_left} day{e.days_left === 1 ? '' : 's'} left
                </span>
                <Button size="sm" onClick={() => restore(e)} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold shrink-0" data-testid={`trash-restore-${e.trash_id}`}>
                  <RotateCcw size={13} /> Restore
                </Button>
                <button onClick={() => setPurging(e)} className="text-gray-500 hover:text-red-400 p-1.5 shrink-0" title="Delete forever" data-testid={`trash-purge-${e.trash_id}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        )}

        <AlertDialog open={Boolean(purging)} onOpenChange={(o) => !o && setPurging(null)}>
          <AlertDialogContent className="bg-[#111111] border-gray-800 text-white" data-testid="trash-purge-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete "{purging?.name}" forever?</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-400">
                All of its data — receipts, photos, documents, expenses, labor, change orders — will be permanently destroyed. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border-gray-700 text-gray-300 hover:bg-[#1A1A1A]">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmPurge} className="bg-red-600 text-white hover:bg-red-700" data-testid="trash-purge-confirm-btn">
                Delete Forever
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};
